import React from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { ConversionMode, ForwardMonth, ForwardTotals, SeriesPoint } from './engine';
import {
  Card,
  ChartTip,
  ConversionControl,
  EmptyRow,
  GroupPicker,
  Metric,
  Pill,
  SearchBox,
  Segmented,
  SortHeader,
  VALUE_OPTIONS,
  ValueBasis
} from './components';
import {
  C,
  blockTone,
  fmtDate,
  fmtDay,
  fmtK,
  fmtMoney,
  fmtNum,
  fmtPct,
  idxToInput,
  occTone
} from './theme';
import { OccupancyTabProps } from './OccupancyTab';
import { Formulas } from './Formulas';

export interface BlockedTabProps extends Omit<OccupancyTabProps, 'drag'> {
  valueBasis: ValueBasis;
  setValueBasis: (v: ValueBasis) => void;
  conversionMode: ConversionMode;
  setConversionMode: (m: ConversionMode) => void;
  fixedRate: number;
  setFixedRate: (r: number) => void;
}

const GROUP_HEADERS = [
  'Group',
  'Units',
  'Block share',
  'Blocked',
  'Total',
  'Expected loss',
  'Exposure',
  'Net occupancy'
];

export function BlockedTab({
  t,
  model,
  groupBy,
  setGroupBy,
  band,
  todayLabel,
  rows,
  sort,
  setSort,
  search,
  setSearch,
  cal,
  valueBasis,
  setValueBasis,
  conversionMode,
  setConversionMode,
  fixedRate,
  setFixedRate
}: BlockedTabProps) {
  const v = (o: ForwardTotals) => (valueBasis === 'expected' ? o.expectedLoss : o.exposure);
  const fwd = model.forward;
  const currentMonth = fwd.currentMonth >= 0 ? cal.months[fwd.currentMonth] : null;
  const yearEndLabel = fmtDate(idxToInput(cal, cal.yearEnd));

  return (
    <div className="mt-5 space-y-5">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-6">
        <Metric
          label="Block share — blocked ÷ total"
          value={fmtPct(t.blockPct)}
          sub={`${fmtNum(t.blocked)} of ${fmtNum(t.total)} nights`}
          accent={C.clay}
        />
        <Metric
          label="Expected loss in range"
          value={`EGP ${fmtK(t.expectedLoss)}`}
          sub={`Exposure EGP ${fmtK(t.exposure)}`}
          accent={C.clay}
        />
        <Metric
          label="Blocked right now"
          value={`${t.unitsBlockedNow} units`}
          sub={`${t.unitsWithBlocks} of ${t.units} blocked in range`}
        />
        <Metric
          label="Blocked this month"
          value={fmtNum(fwd.currentMonthBlocked)}
          sub={currentMonth ? `${currentMonth.long}, whole month` : '—'}
        />
        <Metric
          label="Forward to year end"
          value={fmtNum(fwd.yearEnd.nights)}
          sub={`EGP ${fmtK(v(fwd.yearEnd))} \xB7 to ${yearEndLabel}`}
          accent={C.clay}
        />
        <Metric
          label="Forward, all on record"
          value={fmtNum(fwd.all.nights)}
          sub={
            fwd.lastBlockDay >= 0
              ? `to ${fmtDay(cal, fwd.lastBlockDay)} \xB7 EGP ${fmtK(v(fwd.all))}`
              : '—'
          }
        />
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <Segmented
          label="Value blocks at"
          value={valueBasis}
          onChange={setValueBasis}
          options={VALUE_OPTIONS}
        />
        {valueBasis === 'expected' && (
          <ConversionControl
            mode={conversionMode}
            setMode={setConversionMode}
            rate={fixedRate}
            setRate={setFixedRate}
          />
        )}
        <p className="text-xs" style={{ color: C.muted }}>
          {valueBasis === 'exposure'
            ? 'Blocked nights priced at the prevailing rate with no conversion assumption — the list value of what came off the market.'
            : conversionMode === 'fixed'
            ? `Blocked nights priced at the prevailing rate, then multiplied by a flat ${fixedRate}% expected occupancy for every night and month.`
            : 'Blocked nights priced at the prevailing rate, then multiplied by the share that unit type actually sold in that month.'}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <Card
            title="Forward block pipeline"
            note="Blocked nights already on the calendar from the report date onward."
          >
            <div className="h-64 px-2 py-4">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={fwd.byMonth} margin={{ top: 5, right: 20, bottom: 5, left: -10 }}>
                  <CartesianGrid stroke="#EEF1F6" vertical={false} />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 11, fill: C.muted }}
                    axisLine={{ stroke: C.line }}
                    tickLine={false}
                  />
                  <YAxis tick={{ fontSize: 11, fill: C.muted }} axisLine={false} tickLine={false} />
                  <Tooltip
                    cursor={{ fill: '#F4F6FA' }}
                    content={
                      <ChartTip
                        rows={(p: ForwardMonth) => [
                          { k: 'Blocked nights', v: fmtNum(p.blocked), color: C.clay },
                          { k: 'Expected loss', v: `EGP ${fmtK(p.expectedLoss)}` },
                          { k: 'Exposure', v: `EGP ${fmtK(p.exposure)}` }
                        ]}
                      />
                    }
                  />
                  <Bar dataKey="blocked" name="Blocked nights" radius={[3, 3, 0, 0]}>
                    {fwd.byMonth.map((m, i) => (
                      <Cell key={m.long} fill={i === 0 ? '#E8C28B' : C.clay} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>
        <Card title="Block share across the window" note="Share of listed nights withheld.">
          <div className="h-64 px-2 py-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={model.series} margin={{ top: 5, right: 12, bottom: 5, left: -14 }}>
                <CartesianGrid stroke="#EEF1F6" vertical={false} />
                <ReferenceArea x1={band.x1} x2={band.x2} fill={C.clay} fillOpacity={0.05} />
                <ReferenceLine x={todayLabel} stroke={C.muted} strokeDasharray="3 3" />
                <XAxis
                  dataKey="key"
                  tickFormatter={(k: string) => model.series.find((m) => m.key === k)?.label ?? k}
                  tick={{ fontSize: 9, fill: C.muted }}
                  axisLine={{ stroke: C.line }}
                  tickLine={false}
                  interval={1}
                />
                <YAxis
                  unit="%"
                  tick={{ fontSize: 10, fill: C.muted }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  cursor={{ fill: '#F4F6FA' }}
                  content={
                    <ChartTip
                      rows={(p: SeriesPoint) => [
                        { k: 'Block share', v: fmtPct(p.blockPct), color: C.clay },
                        { k: 'Blocked nights', v: fmtNum(p.blocked) },
                        { k: 'Net occupancy', v: fmtPct(p.net), color: C.green }
                      ]}
                    />
                  }
                />
                <Bar dataKey="blockPct" radius={[3, 3, 0, 0]} fill={C.clay} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <Card
        title="Blocked nights by group"
        right={<GroupPicker groupBy={groupBy} setGroupBy={setGroupBy} />}
      >
        <div className="max-h-[360px] overflow-auto">
          <table className="w-full text-sm">
            <thead
              className="sticky top-0 border-b"
              style={{ borderColor: C.line, background: '#FBFCFE' }}
            >
              <tr>
                {GROUP_HEADERS.map((h, i) => (
                  <th
                    key={h}
                    className={`px-4 py-3 text-[11px] font-semibold ${
                      i === 0 || i === 2 || i === 7 ? 'text-left' : 'text-right'
                    }`}
                    style={{ color: C.muted }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {model.groups
                .slice()
                .sort((a, b) => b.expectedLoss - a.expectedLoss)
                .map((g) => (
                  <tr
                    key={g.key}
                    className="border-b last:border-0"
                    style={{ borderColor: C.line }}
                  >
                    <td className="px-4 py-3 font-medium">{g.key}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{g.units}</td>
                    <td className="px-4 py-3">
                      <Pill value={g.blockPct} tone={blockTone} />
                    </td>
                    <td
                      className="px-4 py-3 text-right tabular-nums"
                      style={{ color: g.blocked ? C.clay : C.muted }}
                    >
                      {fmtNum(g.blocked)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">{fmtNum(g.total)}</td>
                    <td
                      className="px-4 py-3 text-right tabular-nums"
                      style={{ color: g.expectedLoss ? C.clay : C.muted }}
                    >
                      {fmtMoney(g.expectedLoss)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums" style={{ color: C.muted }}>
                      {fmtMoney(g.exposure)}
                    </td>
                    <td className="px-4 py-3">
                      <Pill value={g.net} tone={occTone} />
                    </td>
                  </tr>
                ))}
              {model.groups.length === 0 && <EmptyRow cols={8} />}
            </tbody>
          </table>
        </div>
      </Card>

      <Card
        title="Blocked dates by unit"
        note={`${rows.length} units in scope`}
        right={<SearchBox search={search} setSearch={setSearch} />}
      >
        <div className="max-h-[560px] overflow-auto">
          <table className="w-full text-sm">
            <thead
              className="sticky top-0 z-10 border-b"
              style={{ borderColor: C.line, background: '#FBFCFE' }}
            >
              <tr>
                <SortHeader column="name" sort={sort} setSort={setSort}>
                  Name
                </SortHeader>
                <SortHeader column="project" sort={sort} setSort={setSort}>
                  Project
                </SortHeader>
                <SortHeader column="area" sort={sort} setSort={setSort}>
                  Area
                </SortHeader>
                <SortHeader column="unitType" sort={sort} setSort={setSort}>
                  Unit type
                </SortHeader>
                <SortHeader column="blockPct" sort={sort} setSort={setSort}>
                  Blocked %
                </SortHeader>
                <SortHeader column="blocked" sort={sort} setSort={setSort} align="right">
                  Nights
                </SortHeader>
                <SortHeader column="blockCount" sort={sort} setSort={setSort} align="right">
                  Blocks
                </SortHeader>
                <SortHeader column="avgBlock" sort={sort} setSort={setSort} align="right">
                  Avg
                </SortHeader>
                <SortHeader column="longestBlock" sort={sort} setSort={setSort} align="right">
                  Longest
                </SortHeader>
                <th
                  className="px-4 py-3 text-left text-[11px] font-semibold"
                  style={{ color: C.muted }}
                >
                  Status
                </th>
                <SortHeader column="expectedLoss" sort={sort} setSort={setSort} align="right">
                  Expected loss
                </SortHeader>
                <SortHeader column="exposure" sort={sort} setSort={setSort} align="right">
                  Exposure
                </SortHeader>
                <SortHeader column="net" sort={sort} setSort={setSort}>
                  Net occupancy
                </SortHeader>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.roomId}
                  className="border-b last:border-0 hover:bg-slate-50"
                  style={{ borderColor: C.line }}
                >
                  <td className="px-4 py-3">
                    <p className="font-medium">{r.name}</p>
                    <p className="text-xs" style={{ color: C.muted }}>
                      {r.id}
                      {' \xB7 '}
                      {r.landlord}
                    </p>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3">{r.project}</td>
                  <td className="whitespace-nowrap px-4 py-3">{r.area}</td>
                  <td className="whitespace-nowrap px-4 py-3">{r.unitType}</td>
                  <td className="px-4 py-3">
                    <Pill value={r.blockPct} tone={blockTone} />
                  </td>
                  <td
                    className="px-4 py-3 text-right tabular-nums"
                    style={{ color: r.blocked ? C.clay : C.muted }}
                  >
                    {r.blocked}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">{r.blockCount || '—'}</td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {r.avgBlock ? r.avgBlock.toFixed(1) : '—'}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">{r.longestBlock || '—'}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-xs">
                    {r.currentBlock ? (
                      <span style={{ color: C.clay }}>
                        {'Day '}
                        {r.currentBlock.runFor}
                        {', '}
                        {r.currentBlock.leftToRun}
                        {' left'}
                        <span
                          className="block"
                          style={{ color: C.muted }}
                          title={r.currentBlock.description || undefined}
                        >
                          {r.currentBlock.reason}
                          {' → '}
                          {fmtDay(cal, r.currentBlock.endsOn)}
                        </span>
                      </span>
                    ) : (
                      <span style={{ color: C.muted }}>Open</span>
                    )}
                  </td>
                  <td
                    className="px-4 py-3 text-right tabular-nums"
                    style={{ color: r.expectedLoss ? C.clay : C.muted }}
                  >
                    {fmtMoney(r.expectedLoss)}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums" style={{ color: C.muted }}>
                    {fmtMoney(r.exposure)}
                  </td>
                  <td className="px-4 py-3">
                    <Pill value={r.net} tone={occTone} />
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <EmptyRow cols={13} />}
            </tbody>
          </table>
        </div>
      </Card>
      <Formulas />
    </div>
  );
}
